import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, Area, AreaChart, ComposedChart } from 'recharts';
import { ChartBar, Download, Filter, Layers } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации данных инвестиций
 */
const InvestmentChart = ({ data, formData, showInflationAdjusted, setShowInflationAdjusted }) => {
  const [chartType, setChartType] = useState('line');
  const [selectedYears, setSelectedYears] = useState([]);
  const [displayData, setDisplayData] = useState(data);
  const [showDetailedTable, setShowDetailedTable] = useState(false);
  const [showAllData, setShowAllData] = useState(false);
  
  // Обновляем данные при изменении входных параметров
  useEffect(() => {
    if (!data || data.length === 0) {
      setDisplayData([]);
      return;
    }
    
    let filteredData = [...data];
    
    // Если выбраны конкретные годы, фильтруем по ним
    if (selectedYears.length > 0) {
      filteredData = filteredData.filter(item => selectedYears.includes(item.year));
    }
    
    // Для больших периодов, показываем только ключевые годы, если не выбран showAllData
    if (data.length > 20 && !showAllData && selectedYears.length === 0) {
      const totalYears = data.length - 1; // Вычитаем начальный год (0)
      
      // Стратегия выборки: показываем начало, конец и промежуточные годы
      filteredData = [data[0]]; // Начальный год всегда
      
      // Добавляем год 1
      if (data.length > 1) {
        filteredData.push(data[1]);
      }
      
      // Добавляем промежуточные годы (каждые 5 лет)
      for (let year = 5; year < totalYears; year += 5) {
        if (data[year]) {
          filteredData.push(data[year]);
        }
      }
      
      // Добавляем последние несколько лет
      for (let year = Math.max(2, totalYears - 3); year <= totalYears; year++) {
        if (data[year] && !filteredData.includes(data[year])) {
          filteredData.push(data[year]);
        }
      }
      
      // Сортируем по годам
      filteredData.sort((a, b) => a.year - b.year);
    }
    
    setDisplayData(filteredData);
  }, [data, selectedYears, showAllData]);
  
  if (!data || data.length === 0) {
    return null;
  }
  
  // Форматирование подписей осей
  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)} млн`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)} тыс`;
    }
    return value;
  };
  
  // Форматирование подписей тултипа
  const formatTooltipValue = (value) => {
    return formatCurrency(value);
  };
  
  // Кастомный тултип для графика
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const yearData = payload[0].payload;
      
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 shadow-md rounded-md">
          <p className="font-medium text-gray-900 dark:text-white">{`Год ${label}`}</p>
          <div className="mt-2 space-y-1">
            <p className="text-sm text-indigo-600 dark:text-indigo-400">
              <span className="font-medium">Баланс:</span> {formatCurrency(yearData.balance)}
            </p>
            {showInflationAdjusted && formData.adjustForInflation && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                <span className="font-medium">С учетом инфляции:</span> {formatCurrency(yearData.inflationAdjustedValue)}
              </p>
            )}
            <p className="text-sm text-green-600 dark:text-green-400">
              <span className="font-medium">Взносы за год:</span> {formatCurrency(yearData.contributions)}
            </p>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              <span className="font-medium">Проценты за год:</span> {formatCurrency(yearData.interestEarned)}
            </p>
            {yearData.taxesPaid > 0 && (
              <p className="text-sm text-red-600 dark:text-red-400">
                <span className="font-medium">Налоги за год:</span> {formatCurrency(yearData.taxesPaid)}
              </p>
            )}
            <div className="pt-1 mt-1 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                <span className="font-medium">Всего вложено:</span> {formatCurrency(yearData.totalContributions)}
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  // Экспорт данных в CSV
  const exportTableToCSV = () => {
    // Определяем заголовки
    const headers = ['Год', 'Дата', 'Вложения за период', 'Проценты', 'Налоги', 'Баланс', 'С учетом инфляции'];
    
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');
    
    const rows = data.map(item => {
      return [
        item.year,
        item.date,
        formatCsvNumber(item.contributions),
        formatCsvNumber(item.interestEarned),
        formatCsvNumber(item.taxesPaid),
        formatCsvNumber(item.balance),
        formatCsvNumber(item.inflationAdjustedValue)
      ].join(';');
    });
    
    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'данные_инвестиций_по_годам.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Рендеринг графика в зависимости от выбранного типа
  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={displayData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
              <XAxis 
                dataKey="year" 
                label={{ value: 'Год', position: 'insideBottom', offset: -5 }} 
              />
              <YAxis 
                tickFormatter={formatYAxis}
                label={{ value: 'Сумма (₽)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="balance" 
                name="Баланс" 
                stroke="#6366f1" 
                strokeWidth={2} 
                dot={{ r: 4 }} 
                activeDot={{ r: 6 }}
              />
              {showInflationAdjusted && formData.adjustForInflation && (
                <Line 
                  type="monotone" 
                  dataKey="inflationAdjustedValue" 
                  name="С учетом инфляции" 
                  stroke="#eab308" 
                  strokeWidth={2} 
                  strokeDasharray="5 5"
                  dot={{ r: 4 }} 
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={displayData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="balance" 
                name="Баланс" 
                stroke="#6366f1" 
                fill="#6366f1" 
                fillOpacity={0.3} 
              />
              {showInflationAdjusted && formData.adjustForInflation && (
                <Area 
                  type="monotone" 
                  dataKey="inflationAdjustedValue" 
                  name="С учетом инфляции" 
                  stroke="#eab308" 
                  fill="#eab308" 
                  fillOpacity={0.2} 
                  strokeDasharray="5 5"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={displayData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="balance" 
                name="Баланс" 
                fill="#6366f1" 
              />
              {showInflationAdjusted && formData.adjustForInflation && (
                <Bar 
                  dataKey="inflationAdjustedValue" 
                  name="С учетом инфляции" 
                  fill="#eab308" 
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'composed':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={displayData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
              <XAxis dataKey="year" />
              <YAxis yAxisId="left" tickFormatter={formatYAxis} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={formatYAxis} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="balance" 
                name="Баланс" 
                fill="#6366f1" 
                stroke="#6366f1"
                fillOpacity={0.2}
              />
              {showInflationAdjusted && formData.adjustForInflation && (
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="inflationAdjustedValue" 
                  name="С учетом инфляции" 
                  stroke="#eab308" 
                  strokeWidth={2}
                  dot={{ r: 3 }} 
                />
              )}
              <Bar 
                yAxisId="right"
                dataKey="interestEarned" 
                name="Проценты за год" 
                fill="#10b981" 
                barSize={20}
              />
              <Bar 
                yAxisId="right"
                dataKey="contributions" 
                name="Взносы за год" 
                fill="#8b5cf6" 
                barSize={20}
              />
            </ComposedChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={displayData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="balance" name="Баланс" stroke="#6366f1" />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <ChartBar className="w-5 h-5 mr-2 text-indigo-500" />
          График роста инвестиций
        </h3>
        
        <div className="flex flex-wrap gap-2">
          {/* Переключатель типа графика */}
          <div className="inline-flex rounded-md shadow-sm">
            {['line', 'area', 'bar', 'composed'].map(type => (
              <button
                key={type}
                onClick={() => setChartType(type)}
                className={`px-3 py-1 text-xs font-medium ${
                  chartType === type
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300'
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                } ${
                  type === 'line' ? 'rounded-l-md' : ''
                } ${
                  type === 'composed' ? 'rounded-r-md' : ''
                } border border-gray-200 dark:border-gray-600`}
              >
                {type === 'line' && 'Линия'}
                {type === 'area' && 'Область'}
                {type === 'bar' && 'Столбцы'}
                {type === 'composed' && 'Комбо'}
              </button>
            ))}
          </div>
          
          {/* Переключатель учета инфляции */}
          {formData.adjustForInflation && (
            <button
              onClick={() => setShowInflationAdjusted(!showInflationAdjusted)}
              className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-md border ${
                showInflationAdjusted
                  ? 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-300 dark:border-yellow-700'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
              }`}
            >
              <Filter className="w-3 h-3 mr-1" />
              {showInflationAdjusted ? 'Скрыть инфляцию' : 'Показать инфляцию'}
            </button>
          )}
          
          {/* Кнопка экспорта */}
          <button
            onClick={exportTableToCSV}
            className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            <Download className="w-3 h-3 mr-1" />
            Экспорт CSV
          </button>
        </div>
      </div>
      
      {/* График */}
      <div className="bg-white dark:bg-gray-800 p-1 rounded-xl overflow-hidden">
        {renderChart()}
      </div>
      
      {/* Фильтр по годам для больших периодов */}
      {data.length > 10 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Layers className="w-4 h-4 mr-1 text-indigo-500" />
              Детализация данных
            </h4>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAllData(!showAllData)}
            >
              {showAllData ? 'Показать ключевые годы' : 'Показать все годы'}
            </Button>
          </div>
        </div>
      )}
      
      {/* Таблица данных */}
      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
            Таблица инвестиций {showDetailedTable ? '(детальная)' : '(краткая)'}
          </h4>
          <Button
            size="sm"
            variant="link"
            onClick={() => setShowDetailedTable(!showDetailedTable)}
          >
            {showDetailedTable ? 'Скрыть детали' : 'Показать детали'}
          </Button>
        </div>
        
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[400px] custom-scrollbar">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold text-center">Год</th>
                <th scope="col" className="px-3 py-2 font-semibold">Дата</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Вложения за период</th>
                {showDetailedTable && (
                  <>
                    <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                    <th scope="col" className="px-3 py-2 font-semibold text-right">Налоги</th>
                  </>
                )}
                <th scope="col" className="px-3 py-2 font-semibold text-right">Баланс</th>
                {formData.adjustForInflation && (
                  <th scope="col" className="px-3 py-2 font-semibold text-right">С учетом инфляции</th>
                )}
                {showDetailedTable && (
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Всего вложено</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayData.map((item) => (
                <tr
                  key={item.year}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
                >
                  <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.year}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {new Date(item.date).toLocaleDateString('ru-RU')}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right">
                    {formatCurrency(item.contributions)}
                  </td>
                  {showDetailedTable && (
                    <>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                        {formatCurrency(item.interestEarned)}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">
                        {formatCurrency(item.taxesPaid)}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-1.5 whitespace-nowrap text-right font-semibold">
                    {formatCurrency(item.balance)}
                  </td>
                  {formData.adjustForInflation && (
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-yellow-600 dark:text-yellow-400">
                      {formatCurrency(item.inflationAdjustedValue)}
                    </td>
                  )}
                  {showDetailedTable && (
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {formatCurrency(item.totalContributions)}
                    </td>
                  )}
                </tr>
              ))}
              
              {/* Итоговая строка */}
              {data.length > 0 && (
                <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                  <td className="px-3 py-2 whitespace-nowrap text-center" colSpan="2">Итого:</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {formatCurrency(data.reduce((sum, item) => sum + item.contributions, 0))}
                  </td>
                  {showDetailedTable && (
                    <>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                        {formatCurrency(data.reduce((sum, item) => sum + item.interestEarned, 0))}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-red-600 dark:text-red-400">
                        {formatCurrency(data.reduce((sum, item) => sum + item.taxesPaid, 0))}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap text-right font-semibold">
                    {formatCurrency(data[data.length - 1].balance)}
                  </td>
                  {formData.adjustForInflation && (
                    <td className="px-3 py-2 whitespace-nowrap text-right text-yellow-600 dark:text-yellow-400">
                      {formatCurrency(data[data.length - 1].inflationAdjustedValue)}
                    </td>
                  )}
                  {showDetailedTable && (
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {formatCurrency(data[data.length - 1].totalContributions)}
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvestmentChart;